import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  History,
  Plus,
  Mail,
  Phone,
  Search,
  Users,
} from 'lucide-react'

import { brokerDirectoryService } from '@/services/brokerDirectory'
import AddContactDialog from './AddContactDialog'
import BrokerBulkImportDialog from './BrokerBulkImportDialog'

export default function BrokerReferenceWorkspace() {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedFirmId, setSelectedFirmId] = useState('')
  const [selectedContactId, setSelectedContactId] = useState('')
  const [historyPage, setHistoryPage] = useState(1)
  const [importOpen, setImportOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const firms = useQuery({ queryKey: ['broker-firms'], queryFn: brokerDirectoryService.listFirms })
  const contacts = useQuery({ queryKey: ['broker-contacts'], queryFn: brokerDirectoryService.listContacts })

  const filteredFirms = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return firms.data ?? []
    const contactFirmIds = new Set(
      (contacts.data ?? [])
        .filter((contact) => `${contact.name} ${contact.email ?? ''}`.toLowerCase().includes(needle))
        .map((contact) => contact.firmId),
    )
    return (firms.data ?? []).filter(
      (firm) => firm.name.toLowerCase().includes(needle) || contactFirmIds.has(firm.id),
    )
  }, [firms.data, contacts.data, search])

  const selectedFirm = (firms.data ?? []).find((firm) => firm.id === selectedFirmId) ?? filteredFirms[0]
  const roster = (contacts.data ?? []).filter((contact) => contact.firmId === selectedFirm?.id)
  const selectedContact = roster.find((contact) => contact.id === selectedContactId) ?? roster[0]
  const history = useQuery({
    queryKey: ['broker-contact-history', selectedContact?.id, historyPage],
    queryFn: () => brokerDirectoryService.getContactHistory(selectedContact!.id, historyPage),
    enabled: Boolean(selectedContact?.id),
  })

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['broker-firms'] }),
      queryClient.invalidateQueries({ queryKey: ['broker-contacts'] }),
    ])
  }

  const loading = firms.isLoading || contacts.isLoading
  const failed = firms.isError || contacts.isError

  return (
    <div className="flex h-full min-h-0 flex-col bg-stone-950">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-400/10 px-5 py-4">
        <div>
          <div className="flex items-center gap-2"><Building2 size={16} className="text-amber-400" /><h1 className="text-sm font-medium text-stone-100">Broker reference directory</h1></div>
          <p className="mt-1 text-[11px] text-stone-500">Brokerage firms are the parent list; contacts live within each firm. Live data only.</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Adding ONE contact by hand had no path here at all — a tenant with
              nobody on file could only bulk-import a spreadsheet, and this is
              the screen they land on. */}
          <button type="button" className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => setAddOpen(true)}>
            <Plus size={13} /> Add contact
          </button>
          <button type="button" className="btn-primary flex items-center gap-1.5 text-[11px]" onClick={() => setImportOpen(true)}>
            <FileSpreadsheet size={13} /> Preview broker list import
          </button>
        </div>
      </header>

      <div className="border-b border-amber-400/10 px-5 py-3">
        <div className="relative max-w-md">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-600" />
          <input aria-label="Search broker directory" className="input w-full pl-8 text-[11px]" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search firm, broker, or email…" />
        </div>
      </div>

      {failed ? (
        <div className="m-5 rounded border border-red-400/20 bg-red-400/[0.04] p-4 text-[12px] text-red-300">The live broker directory could not be loaded. No demo contacts are shown.</div>
      ) : loading ? (
        <div className="p-5 text-[12px] text-stone-500">Loading broker directory…</div>
      ) : (firms.data ?? []).length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-lg rounded-xl border border-amber-400/15 bg-black/20 p-6 text-center">
            <Building2 size={28} className="mx-auto text-amber-400/70" />
            <h2 className="mt-3 text-sm font-medium text-stone-100">No broker list has been loaded</h2>
            <p className="mt-2 text-[11px] leading-relaxed text-stone-500">The importer is ready, but Antarctica&apos;s actual brokerage and contact file has not been supplied. The directory stays empty rather than using placeholders or guessed records.</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <button type="button" className="btn-primary text-[11px]" onClick={() => setImportOpen(true)}>Choose the client file and preview</button>
              <button type="button" className="btn-secondary flex items-center gap-1.5 text-[11px]" onClick={() => setAddOpen(true)}><Plus size={12} /> Add one by hand</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-[minmax(220px,0.65fr)_minmax(300px,0.9fr)_minmax(360px,1.2fr)]">
          <aside className="overflow-y-auto border-r border-amber-400/10">
            {filteredFirms.map((firm) => (
              <button
                key={firm.id}
                type="button"
                className={`flex w-full items-center justify-between border-b border-amber-400/5 px-4 py-3 text-left ${selectedFirm?.id === firm.id ? 'bg-amber-400/[0.08]' : 'hover:bg-white/[0.02]'}`}
                onClick={() => {
                  setSelectedFirmId(firm.id)
                  setSelectedContactId('')
                  setHistoryPage(1)
                }}
              >
                <div><div className="text-[12px] font-medium text-stone-200">{firm.name}</div><div className="mt-0.5 text-[10px] text-stone-600">{[firm.city, firm.state].filter(Boolean).join(', ') || 'Location not recorded'}</div></div>
                <span className="rounded-full border border-amber-400/10 px-2 py-0.5 text-[10px] text-stone-500">{firm.contactCount}</span>
              </button>
            ))}
          </aside>
          <section className="min-h-0 overflow-y-auto border-r border-amber-400/10 p-4">
            {selectedFirm && (
              <>
                <div className="flex items-start justify-between">
                  <div><h2 className="text-base font-medium text-stone-100">{selectedFirm.name}</h2><p className="mt-1 text-[11px] text-stone-500">{selectedFirm.emailDomains.join(', ') || 'No verified email domain'}</p></div>
                  <span className="flex items-center gap-1.5 text-[11px] text-stone-500"><Users size={13} /> {roster.length} contacts</span>
                </div>
                <div className="mt-4 overflow-hidden rounded-lg border border-amber-400/10">
                  {roster.length === 0 ? (
                    <div className="p-5 text-[11px] text-stone-500">No contacts are linked to this firm yet.</div>
                  ) : roster.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      className={`grid w-full grid-cols-[1fr_auto] gap-3 border-b border-amber-400/5 px-3 py-2.5 text-left last:border-0 ${selectedContact?.id === contact.id ? 'bg-amber-400/[0.07]' : 'hover:bg-white/[0.02]'}`}
                      onClick={() => {
                        setSelectedContactId(contact.id)
                        setHistoryPage(1)
                      }}
                    >
                      <div>
                        <div className="text-[12px] text-stone-200">{contact.name}</div>
                        <div className="text-[10px] text-stone-600">{contact.title || 'Title not recorded'}</div>
                        <div className="mt-1 space-y-1 text-[10px] text-stone-500">{contact.email && <div className="flex items-center gap-1"><Mail size={10} />{contact.email}</div>}{contact.phone && <div className="flex items-center gap-1"><Phone size={10} />{contact.phone}</div>}</div>
                      </div>
                      <span className="self-start rounded border border-emerald-400/15 bg-emerald-400/[0.04] px-2 py-0.5 text-[9px] text-emerald-300">{contact.relationshipStatus || 'Active'}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
          <main className="min-h-0 overflow-y-auto p-5">
            {!selectedContact ? (
              <div className="flex h-full items-center justify-center text-[11px] text-stone-600">Select a contact to view their history.</div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4 border-b border-amber-400/10 pb-4">
                  <div>
                    <div className="flex items-center gap-2"><History size={15} className="text-amber-400" /><h2 className="text-sm font-medium text-stone-100">{selectedContact.name}</h2></div>
                    <p className="mt-1 text-[10px] text-stone-500">Complete submission and deal history, derived from live intake and loan records.</p>
                  </div>
                  <span className="rounded-full border border-amber-400/15 px-2 py-1 text-[10px] text-stone-400">{history.data?.total ?? 0} events</span>
                </div>
                {history.isLoading ? (
                  <div className="py-6 text-[11px] text-stone-500">Loading contact history…</div>
                ) : history.isError ? (
                  <div className="mt-4 rounded border border-red-400/20 bg-red-400/[0.04] p-3 text-[11px] text-red-300">Contact history could not be loaded.</div>
                ) : (history.data?.items.length ?? 0) === 0 ? (
                  <div className="py-8 text-center text-[11px] text-stone-600">No linked submissions or deals yet.</div>
                ) : (
                  <div className="divide-y divide-amber-400/5">
                    {history.data?.items.map((item) => (
                      <div key={`${item.kind}-${item.id}`} className="py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="rounded border border-amber-400/15 bg-amber-400/[0.04] px-1.5 py-0.5 text-[9px] uppercase tracking-wide text-amber-300">{item.kind}</span>
                              {item.archived ? <span className="text-[9px] uppercase text-stone-600">Archived</span> : null}
                            </div>
                            <div className="mt-1 truncate text-[12px] text-stone-200">{item.subject || item.property || item.id}</div>
                            <div className="mt-1 text-[10px] text-stone-500">
                              {[item.sourceEmail, item.stage, item.killReason].filter(Boolean).join(' · ') || 'No additional detail'}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="text-[10px] capitalize text-stone-400">{item.status || 'recorded'}</div>
                            <div className="mt-1 text-[9px] text-stone-600">{item.occurredAt ? new Date(item.occurredAt).toLocaleString() : 'Date unavailable'}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(history.data?.pages ?? 0) > 1 ? (
                  <div className="mt-4 flex items-center justify-between border-t border-amber-400/10 pt-3 text-[10px] text-stone-500">
                    <button type="button" className="btn-secondary flex items-center gap-1 px-2 py-1 text-[10px]" disabled={historyPage <= 1} onClick={() => setHistoryPage((page) => Math.max(1, page - 1))}><ChevronLeft size={11} /> Previous</button>
                    <span>Page {history.data?.page} of {history.data?.pages}</span>
                    <button type="button" className="btn-secondary flex items-center gap-1 px-2 py-1 text-[10px]" disabled={historyPage >= (history.data?.pages ?? 0)} onClick={() => setHistoryPage((page) => page + 1)}>Next <ChevronRight size={11} /></button>
                  </div>
                ) : null}
              </>
            )}
          </main>
        </div>
      )}

      <BrokerBulkImportDialog open={importOpen} onClose={() => setImportOpen(false)} onCommitted={refresh} />
      {addOpen && (
        <AddContactDialog
          onClose={() => {
            setAddOpen(false)
            void refresh()
          }}
        />
      )}
    </div>
  )
}
